import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { updateLanguage } from '../features/api/user';

const SUPPORTED_LANGS = ['en', 'ka'];

// Public page names (without /:lang/ prefix)
const PUBLIC_PAGE_NAMES = ['about', 'contact', 'privacy', 'terms', 'cookies'];

const languages = [
  { code: 'en', label: 'EN' },
  { code: 'ka', label: 'KA' },
] as const;

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);

    const parts = location.pathname.split('/');

    if (location.pathname === '/') {
      // Landing page — no URL change, just i18n state
    } else if (parts.length >= 2 && SUPPORTED_LANGS.includes(parts[1])) {
      // URL has language prefix (e.g. /ka/about, /en/dashboard) — update it
      parts[1] = code;
      navigate(parts.join('/') + location.search, { replace: true });
    } else if (parts.length === 2 && PUBLIC_PAGE_NAMES.includes(parts[1])) {
      // Public page without lang prefix (e.g. /about) — add prefix
      navigate(`/${code}/${parts[1]}` + location.search, { replace: true });
    } else {
      // Other pages — add language prefix
      parts.splice(1, 0, code);
      navigate(parts.join('/') + location.search, { replace: true });
    }

    // Sync language preference with backend (fire-and-forget)
    updateLanguage(code).catch((err) => {
      console.warn('Failed to sync language preference:', err);
    });
  };

  return (
    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
      {languages.map(({ code, label }) => (
        <button
          key={code}
          onClick={() => handleLanguageChange(code)}
          className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
            i18n.language === code
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
